import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSetCurrentPage, useAppStore } from '../stores/useAppStore';
import { useWatchlistStore } from '../stores/useWatchlistStore';
import { watchlistService, WatchlistBatchBacktestStatus } from '../services/watchlistService';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';

const Watchlist: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [batchStatus, setBatchStatus] = useState<WatchlistBatchBacktestStatus | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [resultsPage, setResultsPage] = useState(1);
  const RESULTS_PAGE_SIZE = 15;
  const setCurrentPage = useSetCurrentPage();
  const setPlaybackSymbol = useAppStore((state) => state.setPlaybackSymbol);
  const { items, isLoading, error, refreshWatchlist, removeFromWatchlist } = useWatchlistStore();
  const refreshWatchlistRef = useRef(refreshWatchlist);
  const hasLoadedRef = useRef(false);
  const batchPollingRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingStoppedRef = useRef(false);
  const hasInitializedBatchStatusRef = useRef(false);
  const showToast = useToastStore((state) => state.show);

  // 更新 ref 中的函数引用
  refreshWatchlistRef.current = refreshWatchlist;

  useEffect(() => {
    // 只在首次挂载时加载，避免重复请求
    if (hasLoadedRef.current) {
      return;
    }

    const loadWatchlist = async () => {
      hasLoadedRef.current = true;
      setLoading(true);
      try {
        await refreshWatchlistRef.current();
      } catch (error) {
        console.error('加载关注列表失败:', error);
        hasLoadedRef.current = false; // 失败时重置，允许重试
      } finally {
        setLoading(false);
      }
    };

    loadWatchlist();
  }, []); // 空依赖数组，只在组件挂载时执行一次

  const getBatchStatusLabel = (status?: WatchlistBatchBacktestStatus['status']) => {
    switch (status) {
      case 'pending':
        return '等待执行';
      case 'running':
        return '回测中';
      case 'completed':
        return '已完成';
      case 'failed':
        return '已失败';
      case 'idle':
      default:
        return '暂无任务';
    }
  };

  const fetchBatchStatus = useCallback(async () => {
    try {
      const status = await watchlistService.getBatchBacktestStatus();
      setBatchStatus(status);
      return status;
    } catch (error) {
      console.error('获取批量回测状态失败:', error);
      return null;
    }
  }, []);

  // 轮询函数：请求返回后再延迟3秒发起下一次请求
  const pollBatchStatus = useCallback(async () => {
    if (isPollingStoppedRef.current) {
      return;
    }

    const status = await fetchBatchStatus();

    if (isPollingStoppedRef.current) {
      return;
    }

    // 检查是否需要继续轮询
    const shouldContinue = status && (status.status === 'pending' || status.status === 'running');

    if (shouldContinue) {
      // 请求返回后延迟3秒再发起下一次请求
      batchPollingRef.current = setTimeout(() => {
        pollBatchStatus();
      }, 3000);
    } else {
      // 任务完成，停止轮询
      isPollingStoppedRef.current = true;
      batchPollingRef.current = null;
    }
  }, [fetchBatchStatus]);

  useEffect(() => {
    // 初始化时获取一次状态（只执行一次）
    if (!hasInitializedBatchStatusRef.current) {
      hasInitializedBatchStatusRef.current = true;
      fetchBatchStatus();
    }

    return () => {
      if (batchPollingRef.current) {
        clearTimeout(batchPollingRef.current);
        batchPollingRef.current = null;
      }
      isPollingStoppedRef.current = true;
    };
  }, [fetchBatchStatus]);

  useEffect(() => {
    // 如果还没有初始化或没有状态，不执行轮询逻辑
    if (!hasInitializedBatchStatusRef.current || !batchStatus) {
      return;
    }

    // 清理之前的轮询
    if (batchPollingRef.current) {
      clearTimeout(batchPollingRef.current);
      batchPollingRef.current = null;
    }
    isPollingStoppedRef.current = false;

    const shouldPoll = batchStatus.status === 'pending' || batchStatus.status === 'running';

    if (shouldPoll) {
      // 延迟启动轮询，避免与初始化请求重复
      // 如果已经有轮询在进行，不重复启动
      if (!batchPollingRef.current) {
        setTimeout(() => {
          if (
            !isPollingStoppedRef.current &&
            batchStatus &&
            (batchStatus.status === 'pending' || batchStatus.status === 'running')
          ) {
            pollBatchStatus();
          }
        }, 3000);
      }
    } else {
      // 任务完成，停止轮询
      isPollingStoppedRef.current = true;
    }

    return () => {
      if (batchPollingRef.current) {
        clearTimeout(batchPollingRef.current);
        batchPollingRef.current = null;
      }
      isPollingStoppedRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchStatus?.status, pollBatchStatus]);

  const handleStartBatchBacktest = async () => {
    if (batchLoading) return;
    if (!items.length) {
      showToast('关注列表为空，无法回测', 'info');
      return;
    }
    try {
      setBatchLoading(true);
      const status = await watchlistService.startBatchBacktest();
      setBatchStatus(status);
      showToast('批量回测任务已启动', 'success');
      fetchBatchStatus();
    } catch (error: any) {
      console.error('批量回测启动失败:', error);
      showToast(error.message || '批量回测启动失败', 'error');
    } finally {
      setBatchLoading(false);
    }
  };

  const isBatchInProgress = batchStatus && (batchStatus.status === 'pending' || batchStatus.status === 'running');

  useEffect(() => {
    if (isBatchInProgress) {
      setResultsPage(1);
    }
  }, [isBatchInProgress]);

  const sortedResults = useMemo(() => {
    if (!batchStatus?.results || batchStatus.results.length === 0) {
      return [];
    }
    return batchStatus.results.slice().sort((a, b) => {
      const dateA = a.finishedAt ? new Date(a.finishedAt).getTime() : 0;
      const dateB = b.finishedAt ? new Date(b.finishedAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [batchStatus?.results]);

  const visibleResults = useMemo(
    () => sortedResults.slice(0, resultsPage * RESULTS_PAGE_SIZE),
    [sortedResults, resultsPage],
  );

  const hasMoreResults = sortedResults.length > visibleResults.length;

  const handleLoadMoreResults = () => {
    setResultsPage((prev) => prev + 1);
  };

  const handleRemoveFromWatchlist = async (symbol: string, instType: string, e?: React.MouseEvent) => {
    e?.stopPropagation(); // 阻止事件冒泡
    try {
      await removeFromWatchlist(symbol, instType);
    } catch (error: any) {
      console.error('取消关注失败:', error.message);
    }
  };

  const handleCardClick = (symbol: string, _instType: string) => {
    setPlaybackSymbol(symbol);
    setCurrentPage('kline-playback');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 计算策略平均总收益率排名
  const strategyRankings = useMemo(() => {
    if (!batchStatus || !batchStatus.results || batchStatus.results.length === 0) {
      return [];
    }

    // 按策略分组，收集每个策略在所有币种上的总收益率
    const strategyMap = new Map<string, { strategyCode: string; strategyName: string; returnRates: number[] }>();

    batchStatus.results.forEach((result) => {
      if (result.status === 'completed' && result.totalReturnPct !== undefined) {
        const key = result.strategyCode;
        if (!strategyMap.has(key)) {
          strategyMap.set(key, {
            strategyCode: result.strategyCode,
            strategyName: result.strategyName,
            returnRates: [],
          });
        }
        strategyMap.get(key)!.returnRates.push(result.totalReturnPct);
      }
    });

    // 计算每个策略的平均总收益率（百分比）
    const rankings = Array.from(strategyMap.entries()).map(([_, data]) => {
      const avgReturnRate = data.returnRates.reduce((sum, r) => sum + r, 0) / data.returnRates.length;
      return {
        strategyCode: data.strategyCode,
        strategyName: data.strategyName,
        avgReturnRate, // 平均总收益率（百分比）
        coinCount: data.returnRates.length,
      };
    });

    // 按平均总收益率降序排序
    return rankings.sort((a, b) => b.avgReturnRate - a.avgReturnRate);
  }, [batchStatus]);

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader
          title="我的关注"
          onBack={() => setCurrentPage('personal-center')}
          rightContent={
            <button
              onClick={handleStartBatchBacktest}
              disabled={batchLoading || Boolean(isBatchInProgress)}
              className="px-3 py-1.5 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
            >
              {isBatchInProgress ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>回测中...</span>
                </>
              ) : (
                '批量回测'
              )}
            </button>
          }
        />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {batchStatus && batchStatus.status !== 'idle' && (
            <div className="mb-4 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-xs text-white/80 space-y-1">
              <div className="flex items-center justify-between">
                <span>批量回测状态：{getBatchStatusLabel(batchStatus.status)}</span>
                {batchStatus.totalTasks > 0 && (
                  <span className="text-white/60">
                    {batchStatus.completedTasks}/{batchStatus.totalTasks}
                  </span>
                )}
              </div>
              {batchStatus.currentTask && batchStatus.status === 'running' && (
                <div className="text-white/60">
                  当前：{batchStatus.currentTask.symbol?.toUpperCase()} · {batchStatus.currentTask.strategyName}
                </div>
              )}
              {batchStatus.error && <div className="text-red-400">错误：{batchStatus.error}</div>}
              {/* 策略平均总收益率排名（仅在回测完成时显示） */}
              {batchStatus.status === 'completed' && strategyRankings.length > 0 && (
                <div className="border-t border-white/10 pt-2 mt-2">
                  <div className="text-xs font-semibold text-white/90 mb-2">策略平均总收益率排名</div>
                  <div className="space-y-1">
                    {strategyRankings.map((ranking, index) => (
                      <div
                        key={ranking.strategyCode}
                        className="flex items-center justify-between text-xs text-white/70"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-white/50 w-4 text-center">{index + 1}</span>
                          <span className="truncate">{ranking.strategyName}</span>
                          <span className="text-white/50">({ranking.coinCount}个币种)</span>
                        </div>
                        <span className={ranking.avgReturnRate >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {ranking.avgReturnRate.toFixed(2)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {visibleResults.length > 0 && (
                <div className="space-y-2 border-t border-white/10 pt-2">
                  <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                    {visibleResults.map((result, index) => (
                      <div
                        key={`${result.symbol}-${result.strategyCode}-${index}`}
                        className="flex items-center justify-between text-white/70 text-sm"
                      >
                        <span className="truncate mr-2">
                          {result.symbol?.toUpperCase()} · {result.strategyName}
                        </span>
                        <span className={result.status === 'completed' ? 'text-green-400' : 'text-red-400'}>
                          {result.status === 'completed'
                            ? `${result.totalReturnPct !== undefined ? result.totalReturnPct.toFixed(2) : '--'}%`
                            : '失败'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs text-white/60 pt-1">
                    <span>{`已加载 ${visibleResults.length}/${sortedResults.length} 条`}</span>
                    {!isBatchInProgress && hasMoreResults ? (
                      <button
                        onClick={handleLoadMoreResults}
                        className="text-blue-300 hover:text-blue-200 transition-colors"
                      >
                        加载更多结果
                      </button>
                    ) : isBatchInProgress && hasMoreResults ? (
                      <span className="text-white/60">正在回测中，列表仅显示第 1 页</span>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
              <div className="text-red-300 text-sm">{error}</div>
            </div>
          )}

          {loading || isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">暂无关注的币种</div>
              <div className="text-gray-500 text-sm">在价格警报页面长按币种可添加关注</div>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={`${item.symbol}-${item.instType}`}
                  onClick={() => handleCardClick(item.symbol, item.instType)}
                  className="bg-white/5 border border-white/20 rounded-xl p-4 text-white/90 mb-2 cursor-pointer hover:bg-white/10 transition-colors"
                >
                  {/* 第一行：币种+类型最大占左，现价高亮占右 */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center min-w-0 flex-1">
                      <h3 className="font-bold text-white text-base truncate max-w-[70%]">{item.symbol}</h3>
                      <span className="flex-shrink-0 text-blue-400 text-xs bg-blue-500/20 px-2 py-0.5 rounded-full ml-2">
                        {item.instType}
                      </span>
                    </div>
                    <span className="font-mono text-lg text-white bg-white/10 px-2 py-0.5 rounded ml-3 flex-shrink-0">
                      {item.currentPrice ?? '-'}
                    </span>
                  </div>
                  {/* 第二行：4周期时间粒度+对应涨跌幅，两端对齐 更大上间距 */}
                  <div className="flex justify-between w-full mt-2 mb-1 text-sm font-mono">
                    <div className="flex items-center">
                      <span className="backdrop-blur-md bg-white/10 rounded px-1 py-0.5 text-xs font-bold text-white/90 mr-0.5">
                        5m
                      </span>
                      <span
                        className={
                          Number(item.change5m) > 0
                            ? 'text-red-400 ml-1'
                            : Number(item.change5m) < 0
                              ? 'text-green-400 ml-1'
                              : 'ml-1'
                        }
                      >
                        {item.change5m !== undefined && item.change5m !== '-' ? `${item.change5m}%` : '-'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="backdrop-blur-md bg-white/10 rounded px-1 py-0.5 text-xs font-bold text-white/90 mr-0.5">
                        1h
                      </span>
                      <span
                        className={
                          Number(item.change1h) > 0
                            ? 'text-red-400 ml-1'
                            : Number(item.change1h) < 0
                              ? 'text-green-400 ml-1'
                              : 'ml-1'
                        }
                      >
                        {item.change1h !== undefined && item.change1h !== '-' ? `${item.change1h}%` : '-'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="backdrop-blur-md bg-white/10 rounded px-1 py-0.5 text-xs font-bold text-white/90 mr-0.5">
                        1d
                      </span>
                      <span
                        className={
                          Number(item.change1d) > 0
                            ? 'text-red-400 ml-1'
                            : Number(item.change1d) < 0
                              ? 'text-green-400 ml-1'
                              : 'ml-1'
                        }
                      >
                        {item.change1d !== undefined && item.change1d !== '-' ? `${item.change1d}%` : '-'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="backdrop-blur-md bg-white/10 rounded px-1 py-0.5 text-xs font-bold text-white/90 mr-0.5">
                        7d
                      </span>
                      <span
                        className={
                          Number(item.change7d) > 0
                            ? 'text-red-400 ml-1'
                            : Number(item.change7d) < 0
                              ? 'text-green-400 ml-1'
                              : 'ml-1'
                        }
                      >
                        {item.change7d !== undefined && item.change7d !== '-' ? `${item.change7d}%` : '-'}
                      </span>
                    </div>
                  </div>
                  {/* 第三行：关注时间+按钮 */}
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-gray-400 text-xs flex-1 truncate">关注时间: {formatDate(item.createdAt)}</div>
                    <button
                      onClick={(e) => handleRemoveFromWatchlist(item.symbol, item.instType, e)}
                      className="px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors text-xs ml-4"
                    >
                      取消关注
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </GradientBackground>
  );
};

export default Watchlist;
